/**
 * The "pages used" step: after the files are read in the browser, what SpecLens will send for
 * analysis, and the chance to change it. Nothing has left the machine at this point.
 */

import type { DrawingPage, LocatedPage, SpecSelection } from "./speclens-reader.ts";
import { selectSpecPages } from "./speclens-reader.ts";
import { DIVISION_NAMES } from "./speclens-view.ts";

export interface SpecRead {
  readonly filename: string;
  /** A .txt or .md spec: no section codes, read whole as Division 27. */
  readonly plain: boolean;
  readonly pages: readonly LocatedPage[];
}

export interface DrawingRead {
  readonly filename: string;
  readonly pages: readonly DrawingPage[];
  /** Selected PDF page numbers. */
  readonly chosen: readonly number[];
}

interface PagesUsedProps {
  readonly specs: readonly SpecRead[];
  readonly drawings: readonly DrawingRead[];
  readonly selection: SpecSelection;
  readonly onSelection: (selection: SpecSelection) => void;
  readonly onDrawingChosen: (filename: string, chosen: readonly number[]) => void;
  readonly busy: boolean;
  readonly analyzing: boolean;
  readonly onAnalyze: () => void;
}

function divisionCounts(specs: readonly SpecRead[]): ReadonlyMap<string, number> {
  const counts = new Map<string, number>();
  for (const spec of specs) {
    if (spec.plain) continue;
    for (const page of spec.pages) {
      if (page.section === undefined) continue;
      const division = page.section.slice(0, 2);
      if (division in DIVISION_NAMES) counts.set(division, (counts.get(division) ?? 0) + 1);
    }
  }
  return counts;
}

export function PagesUsed({ specs, drawings, selection, onSelection, onDrawingChosen, busy, analyzing, onAnalyze }: PagesUsedProps) {
  const counts = divisionCounts(specs);
  const specTotal = specs.reduce((sum, spec) => sum + spec.pages.length, 0);
  const specChosen = specs.reduce((sum, spec) => sum + (spec.plain ? spec.pages.length : selectSpecPages(spec.pages, selection).length), 0);
  const drawingChosen = drawings.reduce((sum, drawing) => sum + drawing.chosen.length, 0);
  const noAudioVisual = specs.some((spec) => !spec.plain) && (counts.get("27") ?? 0) === 0;

  const toggleDivision = (division: string, on: boolean) => {
    const divisions = on ? [...selection.divisions, division] : selection.divisions.filter((have) => have !== division);
    onSelection({ ...selection, divisions });
  };

  return (
    <section className="sl-card">
      <h2 className="sl-card__title">Pages used</h2>

      {specs.length > 0 && (
        <>
          <p className="hint">
            Specification: using {specChosen} of {specTotal} pages. The rest (cabinetry, structure, finishes you do not need) stays
            on this computer.
          </p>
          {[...counts].sort().map(([division, count]) => (
            <label key={division} className="sl-check">
              <input
                type="checkbox"
                checked={selection.divisions.includes(division)}
                onChange={(event) => toggleDivision(division, event.target.checked)}
              />
              <span>
                Division {division} - {DIVISION_NAMES[division]} <span className="hint">({count} pages)</span>
              </span>
            </label>
          ))}
          {counts.has("26") && (
            <label className="sl-check sl-check--sub">
              <input
                type="checkbox"
                checked={selection.limitDivision26}
                onChange={(event) => onSelection({ ...selection, limitDivision26: event.target.checked })}
              />
              <span>Division 26: only the audio-visual wiring and device sections</span>
            </label>
          )}
          {noAudioVisual && (
            <p className="sl__problem" role="alert">
              No Division 27 section was found in this book. Check that you chose the volume with the communications sections.
            </p>
          )}
        </>
      )}

      {drawings.map((drawing) => (
        <details key={drawing.filename} className="sl-sheets">
          <summary>
            {drawing.filename}: {drawing.chosen.length} of {drawing.pages.length} sheets
          </summary>
          <div className="sl-sheets__list">
            {drawing.pages.map((page) => (
              <label key={page.page} className="sl-chip">
                <input
                  type="checkbox"
                  checked={drawing.chosen.includes(page.page)}
                  onChange={(event) =>
                    onDrawingChosen(
                      drawing.filename,
                      event.target.checked ? [...drawing.chosen, page.page] : drawing.chosen.filter((have) => have !== page.page),
                    )
                  }
                />
                <span>{page.sheet ?? `p${page.page}`}</span>
              </label>
            ))}
          </div>
        </details>
      ))}

      <button type="button" className="btn btn--primary" disabled={busy || specChosen + drawingChosen === 0} onClick={onAnalyze}>
        {analyzing ? "Analyzing..." : "Analyze these pages"}
      </button>
    </section>
  );
}
